<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import EzTable from '@/components/ui/Table';
import EzEntityInfo from '@/components/ui/EntityInfo';
import { EzCsvUploadModal } from '@/components/ui/Modal';
import EzButton, { EzButtonGroup } from '@/components/ui/Button';
import ProposePriceModal from '@/views/platform/venue/suppliers/all/ProposePriceModal';
import EzProductModal from '@/views/common/products/new-product-supplier/EzProductModal';
import flash from '@/components/ui/FlashMessage';
import { falsy } from '@/util/utils';
import confirmation from '@/components/v3/patterns/VConfirmation/control';
import EzLoadMore from '@/components/ui/LoadMore/EzLoadMore';
import EmptyState from '@/views/common/empty-state/EmptyState';
import VStatusDropdown from '@/components/v3/patterns/VStatusDropdown';
import EzSimpleDropdown from '@/components/ui/Dropdown/EzSimpleDropdown';
import { wizardListenerMixin } from '@/mixins/wizard';
import { LOADING_KEY } from '@/util/constants';
import VDataWithInfo from '@/components/v3/elements/VDataWithInfo';
import VPrice from '@/components/v3/elements/VPrice';

export default {
  mixins: [wizardListenerMixin],
  components: {
    EzCsvUploadModal,
    EzButtonGroup,
    ProposePriceModal,
    EzProductModal,
    EzButton,
    EzTable,
    EzLoadMore,
    EmptyState,
    VStatusDropdown,
    EzSimpleDropdown,
    EzEntityInfo,
    VDataWithInfo,
    VPrice,
  },
  props: {
    supplierId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      headers: {
        name: () => 'Product',
        price: () => 'Price per unit',
        actions: () => '',
      },
      products: [],
      selectedProduct: {},
      meta: {},
    };
  },
  computed: {
    ...mapState('entities/distributors', ['selectedSupplier']),
    ...mapState('entities/users', ['contextId']),
    ...mapGetters('loading', ['getLoading']),
    isLoading() {
      return this.getLoading(LOADING_KEY.VENUE_HIDE_UNHIDE_PRODUCTS);
    },
    venueId() {
      return this.contextId;
    },
    distributor() {
      return this.selectedSupplier || {};
    },
    editAction() {
      return `/venue/distributors/${this.supplierId}/products`;
    },
    columns() {
      return ['name', 'sku', 'category', 'visibility', 'unit', 'pricePerUnit', 'actions'];
    },
  },
  methods: {
    ...mapActions('entities/products', [
      'venueFetchNewDistributorProducts',
      'venueFetchNewDistributorProduct',
      'venueRemoveNewDistributorProduct',
      'venueUploadDistributorProducts',
      'venueHideDistributorProduct',
      'venueUnhideDistributorProduct',
      'venueHideAllDistributorProduct',
      'venueUnhideAllDistributorProduct',
    ]),
    ...mapActions('entities/categories', ['fetchCategoriesByVenueId']),
    proposePrice(product) {
      this.selectedProduct = product;
      this.$refs.modal.open();
    },
    newProduct() {
      this.selectedProduct = {};
      this.openEditModal();
    },
    closeEditModal() {
      this.$refs.productModal.close();
    },
    openEditModal() {
      this.$refs.productModal.open();
    },
    async openSingleProduct(product) {
      const { editable, id } = product;
      if (!editable) return;
      const { data } = await this.venueFetchNewDistributorProduct({
        venueId: this.venueId,
        distributorId: this.supplierId,
        productId: id,
      });
      this.selectedProduct = data.data;
      this.openEditModal();
    },
    onModalSuccess() {
      flash.success({
        title: `Product successfully ${falsy(this.selectedProduct) ? 'added' : 'updated'}.`,
      });
      this.selectedProduct = {};
      this.closeEditModal();
      this.refresh();
    },
    onPriceChange() {
      flash.success({
        title: 'New price successfully set',
        message: `You have set a new price for ${this.selectedProduct.name}`,
      });
      this.refresh();
    },
    async removeProduct({ id }) {
      const { close, canceled } = await confirmation.alert({
        title: this.$t('product.removeProductAlertTitle'),
        message: this.$t('product.removeProductAlertMessage'),
      });
      if (canceled) return;
      close();

      try {
        await this.venueRemoveNewDistributorProduct({
          venueId: this.venueId,
          distributorId: this.supplierId,
          productId: id,
        });
        flash.success({ title: 'Product successfully removed.' });
        await this.refresh();
      } catch (e) {
        flash.error({ title: 'Something went wrong' });
      }
    },
    fetchNewDistributorProducts() {
      return this.venueFetchNewDistributorProducts({
        distributorId: this.supplierId,
        query: {
          ...(this.meta.nextId ? { nextId: this.meta.nextId, limit: '20' } : { limit: '20' }),
        },
      });
    },
    async refresh() {
      this.meta = {};
      const { data } = await this.fetchNewDistributorProducts();
      this.products = data.data;
      this.meta = data.meta;
    },
    async loadMore() {
      const { data } = await this.fetchNewDistributorProducts();
      this.products = [...this.products, ...data.data];
      this.meta = data.meta;
    },
    onLoadMore() {
      this.loadMore();
    },
    uploadCSVModal() {
      this.$refs.csvUploadModal.open();
    },
    uploadCSVAction(data) {
      return this.venueUploadDistributorProducts({
        venueId: this.venueId,
        distributorId: this.supplierId,
        data,
      });
    },
    onUploadFinished() {
      this.refresh();
    },
    updateProductAvailability(venue) {
      const { id, isHidden } = venue;
      const data = {
        productId: id,
        venueId: this.venueId,
        distributorId: this.supplierId,
      };
      return isHidden
        ? this.venueUnhideDistributorProduct(data)
        : this.venueHideDistributorProduct(data);
    },
    async updateVisibility(venue) {
      try {
        await this.updateProductAvailability(venue);
        this.refresh();
      } catch (e) {
        flash.error({
          title: 'Something went wrong!',
        });
      }
    },
    async bulkUpdateProductVisibility(isHidden) {
      try {
        const data = {
          venueId: this.venueId,
          distributorId: this.supplierId,
        };
        await (isHidden
          ? this.venueHideAllDistributorProduct(data)
          : this.venueUnhideAllDistributorProduct(data));
        this.refresh();
      } catch (e) {
        flash.error({
          title: 'Something went wrong!',
        });
      }
    },
    selectAll() {
      this.bulkUpdateProductVisibility(false);
    },
    deselectAll() {
      this.bulkUpdateProductVisibility(true);
    },
    onNextStep() {
      this.$router.push({ name: 'venue-all-suppliers' });
    },
  },
  async mounted() {
    await this.fetchCategoriesByVenueId(this.venueId);
    await this.refresh();
    this.enableNextStep();
  },
};
</script>
<template>
  <div>
    <ez-product-modal
      ref="productModal"
      formKey="product-edit-modal"
      :formAction="editAction"
      :distributorId="supplierId"
      :product="selectedProduct"
      @success="onModalSuccess"
    />

    <ez-csv-upload-modal
      ref="csvUploadModal"
      :uploadCSV="uploadCSVAction"
      :onUploadFinished="onUploadFinished"
    />

    <propose-price-modal
      ref="modal"
      :venueId="venueId"
      :distributorId="supplierId"
      :product="selectedProduct"
      @success="onPriceChange"
    >
      <template #title> Set New Pricing </template>
      <template #text>
        <p>Please type in the price you would like to set for {{ selectedProduct.name }}.</p>
      </template>
      <template #confirm> Set New Price </template>
    </propose-price-modal>

    <ez-button-group class="header mb-24">
      <ez-simple-dropdown placeholder="Set Visibility">
        <template #dropdown>
          <div class="dropdown-actions">
            <ez-button type="link" @click="selectAll">
              <font-awesome-icon icon="eye" class="mr-8" /><span>Show All</span>
            </ez-button>
            <ez-button type="link" @click="deselectAll">
              <font-awesome-icon icon="eye-slash" class="mr-8" /><span>Hide All</span>
            </ez-button>
          </div>
        </template>
      </ez-simple-dropdown>
      <span class="ez-button-group__line"></span>

      <ez-button type="secondary" @click="uploadCSVModal">Upload CSV</ez-button>
      <ez-button @click="newProduct">
        <span>Add New Product</span>
      </ez-button>
    </ez-button-group>

    <ez-table
      class="products-table"
      v-if="products.length"
      :data="products"
      :headers="headers"
      :columns="columns"
      :removeButton="true"
      @rowClick="openSingleProduct"
      @removeItem="removeProduct"
      :columnProps="{
        sku: { class: 'sku-cell' },
        category: { class: 'category-cell' },
        visibility: { class: 'visibility-cell' },
        pricePerUnit: { class: 'price-cell' },
        unit: { class: 'medium-cell' },
        actions: { class: 'actions-cell' },
      }"
      :loading="isLoading"
    >
      <template #cell-name="{ row }">
        <ez-entity-info imgWidth="2rem" imgHeight="2rem" :imgUrl="row.image">
          <div class="product-info" :title="row.name">
            <span>{{ row.name }}</span>
            <span class="product-info-secondary">
              {{ row.sku }} &#8226; {{ row.category | categoryWithParent }}
            </span>
          </div>
        </ez-entity-info>
      </template>
      <template #cell-visibility="{ row }">
        <v-status-dropdown
          @change="updateVisibility(row)"
          :data="$helpers.getVisibilityOptions(row.isHidden)"
        />
      </template>
      <template #cell-unit="{ row: { orderingUnit } }">
        <template v-if="orderingUnit">
          <v-data-with-info
            :info="$t(`product.unitInfo.${orderingUnit.type}`)"
            :show-underline="!!$t(`product.unitInfo.${orderingUnit.type}`)"
          >
            {{ orderingUnit.label }}
          </v-data-with-info>
        </template>
      </template>
      <template #cell-pricePerUnit="{ row: { price, priceUnit, marketPrice } }">
        <v-price
          :price="price || 0"
          :unit="priceUnit && priceUnit.label"
          :is-market-price="marketPrice"
        />
      </template>
      <template #cell-actions="{ row }">
        <ez-button v-if="row.proposedPrice" type="green">Price proposed</ez-button>
        <ez-button v-else type="secondary" @click.stop="proposePrice(row)" :stop-propagation="true">
          Set Price
        </ez-button>
      </template>
    </ez-table>
    <template v-else>
      <empty-state class="empty-state-center">
        <template #badge> <img src="@/assets/no-product-empty-state.svg" alt="" /></template>
        <template #title>No products listed</template>
      </empty-state>
    </template>
    <ez-load-more v-if="meta.nextId" @loadMore="onLoadMore" />
    <ez-button class="mt-24" type="secondary" formType="button" isFullWidth @click="newProduct">
      <font-awesome-icon icon="plus" />
      <slot name="add-new-button">Add New Product</slot>
    </ez-button>
  </div>
</template>

<style lang="scss" scoped>
.header {
  justify-content: flex-end;
}

:deep() .actions-cell {
  width: 170px;
}

:deep() .products-table {
  tbody tr td {
    overflow: visible;
  }
}

.product-info {
  display: flex;
  flex-direction: column;
}

.product-info--secondary {
  display: block;
}
:deep() .table {
  .sku-cell,
  .category-cell {
    display: none;
  }

  tbody tr td {
    line-height: 1.5;
  }
}

:deep() .visibility-cell {
  width: 100px;
}

:deep() .loader {
  position: fixed;
}
</style>
